import { fromUrl, parseDomain, ParseResultType } from 'parse-domain';
import { difference } from 'lodash';
interface IParseDomainOptions {
  reservedSubDomains?: string[];
}
const SUBDOMAIN_RESERVED = ['app', 'qa-app', 'dashboard', 'qa-dashboard', 'pentest-dashboard', 'blue-dashboard',
// commutifi-fe subdomains are just temporary while we setup monorepo deployments
'qa-commutifi-fe-public-ai', 'commutifi-fe-public-ai', 'qa-ai', 'ai', 'qa-commutifi-fe-web', 'commutifi-fe-web'];

// Reserved together with every subdomain nested under them: the mock app is hosted on both
// dash.commutifi.com and *.dash.commutifi.com, and none of those hosts is a tenant.
const SUBDOMAIN_RESERVED_WITH_NESTED = ['dash'];

/**
 * Extract a list of subDomains present in the parsed URL
 * @param url - URL to parse
 */
export const extractSubDomains = (url: string | null) => {
  if (!url) return [];
  const parsedDomain = parseDomain(fromUrl(url));
  const subdomains = parsedDomain.type === ParseResultType.Listed ? parsedDomain.subDomains : [];
  return subdomains;
};

/**
 * Extract a list of subDomains that are not reserved for our app
 * @param url - URL to parse
 */
export const getUnreservedSubDomains = (url: string | null, options: IParseDomainOptions = {
  reservedSubDomains: SUBDOMAIN_RESERVED
}): string[] => {
  if (!url) {
    return [];
  }
  const subdomains = extractSubDomains(url);
  // Subdomains are listed outermost first, so the one right above the root domain is last
  const rootSubdomain = subdomains[subdomains.length - 1];
  if (rootSubdomain && SUBDOMAIN_RESERVED_WITH_NESTED.includes(rootSubdomain)) {
    return [];
  }
  return subdomains.length > 0 ? difference(subdomains, options.reservedSubDomains || []) : [];
};
export const isReservedSubDomain = (url: string) => getUnreservedSubDomains(url).length === 0;

/**
 * The tenant subdomain for the page currently being viewed, or undefined on the root domain.
 *
 * This is the single source for "which enterprise are we on". It reads the URL rather than any API
 * response, so it is available before — and independently of — the request that resolves the
 * enterprise: `/subdomains` resolves the legacy identity-provider record and answers 401 without
 * one, and callers still need the subdomain in that case.
 */
export const getUrlSubdomain = (): string | undefined => getUnreservedSubDomains(window.location.href)[0];